// CMDB Portal sidebar — pruned to the items that lead somewhere useful (UC2).
//
// Kept: Search, Catalog, Catalog Graph (via nav.rest fallthrough), Settings.
// Dropped from the default `nav.rest`: scaffolder/Create, APIs, Docs (TechDocs),
// Kubernetes, Notifications, Register Existing, Visualizer — they correspond
// to backend plugins removed in UC4 or to surfaces this portal does not own.
//
// `nav.take(...)` consumes an item by extension name so it does not fall through
// into `nav.rest({})`. `nav.rest({})` renders whatever is left in the registry,
// which after UC2+UC4 should only be `page:catalog-graph` and built-ins.

import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import BusinessIcon from '@material-ui/icons/Business';
import AppsIcon from '@material-ui/icons/Apps';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import ComputerIcon from '@material-ui/icons/Computer';
import AssignmentIcon from '@material-ui/icons/Assignment';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import SecurityIcon from '@material-ui/icons/Security';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skip the built-in search page (we use the modal instead).
      nav.take('page:search');

      // Skip nav items whose backends were removed in UC4 or that lead to
      // empty / not-applicable pages for a CMDB portal. take() consumes them
      // without rendering so they do not appear via nav.rest below.
      nav.take('page:scaffolder');               // Create (no templates)
      nav.take('page:api-docs');                 // APIs (no API entities)
      nav.take('page:kubernetes');               // K8s (out of scope)
      nav.take('page:notifications');            // notifications-backend gone
      nav.take('page:catalog-import');           // Register Existing
      nav.take('page:app-visualizer');           // Visualizer
      // Global Catalog Graph requires a focal entity + the default relation
      // filter targets API/Component kinds we don't have. Per-entity Catalog
      // Graph TAB on each Resource page is strictly more useful for our data,
      // so the global page just creates a wrong-direction click. Drop it from
      // the sidebar; the per-entity tab is unaffected (Fix B from operator UX
      // feedback 2026-05-19).
      nav.take('page:catalog-graph');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {nav.take('page:catalog')}
            {/* Wave 4.1: Agencies overview page — sortable table of every
                agency with user/endpoint/open-signal counts. Sits above
                the catalog-graph fallthrough so it's reachable in one
                click from any page. */}
            <SidebarItem icon={BusinessIcon} text="Agencies" to="/agencies" />
            {/* 2026-06-02: Hardware overview — every endpoint with
                Defender + Intune posture, sortable + searchable. */}
            <SidebarItem icon={ComputerIcon} text="Hardware" to="/hardware" />
            {/* 2026-05-21: Software inventory + active usage (last 7d). */}
            <SidebarItem icon={AppsIcon} text="Software" to="/software" />
            {/* 2026-07-29: IT spend from NetSuite — every accounting line for
                the IT GL accounts with vendor/category/agency/department.
                RESTORED 2026-07-30 (second attempt). The operator reported the
                Finance tab missing TWICE. The first fix restored only the
                PageBlueprint route in App.tsx — but per the note below, the
                nav.rest({}) fallthrough was removed on 2026-06-25, so a page
                extension renders NO nav item on its own. Route without a
                SidebarItem == reachable by URL, invisible in the UI. */}
            <SidebarItem icon={AccountBalanceIcon} text="Finance" to="/finance" />
            {/* 2026-09-11: APIM inventory — APIs, their gateways, their
                backends and what is ungoverned. NOTE this is OUR page at
                /apis; Backstage's stock "APIs" item (page:api-docs) stays
                consumed by nav.take above. Two nav items with the same label
                going to different places is the failure this note exists to
                prevent, and apiHelpers.test.ts asserts the take() is still
                there. */}
            <SidebarItem icon={SettingsEthernetIcon} text="APIs" to="/apis" />
            {/* 2026-06-12: Change/Release Management (POC). File + manage
                change requests natively in the CMDB, in parallel with the
                ADO Boards path. URLs under /changes/* activate this item. */}
            <SidebarItem icon={AssignmentIcon} text="Change Mgmt" to="/changes" />
            {/* 2026-09-28: Agency Migration tracker — register a migrating
                agency (MIG-xxxx + a cross-system trace id) and mark it
                complete. Directly below Change Mgmt at the operator's request;
                agencyMigrationHelpers.test.ts pins that position. */}
            <SidebarItem icon={SwapHorizIcon} text="Agency Migration" to="/agency-migrations" />
            {/* 2026-10-09: Continuity register mock (dev only; the API 404s
                elsewhere and the page says so). continuityHelpers.test.ts
                asserts this item, the route and the module entry. */}
            <SidebarItem icon={SecurityIcon} text="Continuity" to="/continuity" />
            {/* The nav.rest({}) fallthrough was REMOVED (2026-06-25): the new
                frontend system auto-discovers a nav item for EVERY page
                extension, and rest() rendered the leftovers — surfacing a stray
                generic "app" item (the root-redirect page's auto nav item,
                titled by its plugin id with the default icon). Every page this
                portal owns is already placed explicitly above, so rest() only
                leaked unwanted auto items. A NEW page must add its own
                SidebarItem here — it will not appear automatically. */}
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          {/* 2026-06-19: Docs — the Change Release Management wiki, rendered
              natively at /docs (WikiPage.tsx; successor to the ADO project wiki,
              replaces TechDocs). Pinned to the BOTTOM of the sidebar, just above
              Settings, per operator request. */}
          <SidebarItem icon={MenuBookIcon} text="Docs" to="/docs" />
          {/* 2026-07-28: Governance (CRM §9 permission model) moved from the
              Menu group to the BOTTOM — with Docs, just above Settings — per
              operator request. It's an admin/config surface, not day-to-day
              nav. */}
          <SidebarItem icon={VerifiedUserIcon} text="Governance" to="/governance" />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
